// src/restaurant/pages/RestaurantDashboard.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import RestaurantSidebar from '../../components/RestaurantSidebar';
import RestaurantHeader from '../../components/RestaurantHeader';
import './RestaurantDashboard.css';
import { useLanguage } from '../../contexts/LanguageContext';

const RestaurantDashboard = () => {
  const { t } = useLanguage();
  const [restaurantData, setRestaurantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    completedOrders: 0,
    pendingOrders: 0,
    todayOrders: 0,
    balance: 0,
  });
  const navigate = useNavigate();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/restaurant/login');
        return;
      }

      const restaurantId = localStorage.getItem('restaurantId');
      if (!restaurantId) {
        navigate('/restaurant/login');
        return;
      }

      try {
        const restaurantDoc = await getDoc(doc(db, 'restaurants', restaurantId));
        if (!restaurantDoc.exists()) {
          console.error('No restaurant found with this ID');
          navigate('/restaurant/login');
          return;
        }

        const restaurant = { id: restaurantDoc.id, ...restaurantDoc.data() };
        setRestaurantData(restaurant);
        await fetchOrderStats(restaurantId);
      } catch (error) {
        console.error('Error fetching restaurant data:', error);
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const fetchOrderStats = async (restaurantId) => {
    try {
      const ordersRef = collection(db, 'orders');
      const q = query(ordersRef, where('restaurantId', '==', restaurantId));
      const querySnapshot = await getDocs(q);
      const orders = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const totalOrders = orders.length;
      const completedOrders = orders.filter(order => order.status === 'تم التسليم ✅').length;
      const pendingOrders = orders.filter(order => order.status !== 'تم التسليم ✅' && order.status !== 'ملغي ❌').length;
      const todayOrders = orders.filter(order => {
        const orderDate = order.createdAt ? new Date(order.createdAt.toDate()) : null;
        return orderDate && orderDate >= today;
      }).length;

      const restaurantDoc = await getDoc(doc(db, 'restaurants', restaurantId));
      const balance = restaurantDoc.data().balance || 0;

      setStats({
        totalOrders,
        completedOrders,
        pendingOrders,
        todayOrders,
        balance
      });
    } catch (error) {
      console.error('Error fetching order stats:', error);
    }
  };

  if (loading) {
    return (
      <div className="restaurant-loading">
        <div className="restaurant-spinner"></div>
        <p>{t('loadingData')}</p>
      </div>
    );
  }

  return (
    <div className="restaurant-dashboard">
      <RestaurantSidebar />
      <div className="restaurant-dashboard-content">
        <RestaurantHeader restaurantName={restaurantData?.name || t('restaurant')} />
        
        <div className="restaurant-dashboard-welcome">
          <h1>{t('welcome')}, {restaurantData?.name || t('restaurant')}!</h1>
          <p>{t('dashboardDescription')}</p>
        </div>

        <div className="restaurant-dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon orders-icon">📦</div>
            <div className="stat-content">
              <h3>{t('totalOrders')}</h3>
              <p className="stat-number">{stats.totalOrders}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon completed-icon">✅</div>
            <div className="stat-content">
              <h3>{t('completedOrders')}</h3>
              <p className="stat-number">{stats.completedOrders}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon pending-icon">🕒</div>
            <div className="stat-content">
              <h3>{t('pendingOrders')}</h3>
              <p className="stat-number">{stats.pendingOrders}</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon today-icon">📅</div>
            <div className="stat-content">
              <h3>{t('todayOrders')}</h3>
              <p className="stat-number">{stats.todayOrders}</p>
            </div>
          </div>
        </div>

        <div className="restaurant-dashboard-balance">
          <div className="balance-card">
            <h3>{t('walletBalance')}</h3>
            <p className="balance-amount">{stats.balance.toFixed(3)} {t('currency')}</p>
            <button className="balance-recharge-btn" onClick={() => navigate('/restaurant/wallet')}>
              {t('rechargeBalance')}
            </button>
          </div>
        </div>

        <div className="restaurant-dashboard-actions">
          <div className="action-card" onClick={() => navigate('/restaurant/orders/new')}>
            <div className="action-icon">🚚</div>
            <h3>{t('sendNewOrder')}</h3>
            <p>{t('sendOrderDescription')}</p>
          </div>

          <div className="action-card" onClick={() => navigate('/restaurant/orders')}>
            <div className="action-icon">📋</div>
            <h3>{t('manageOrders')}</h3>
            <p>{t('manageOrdersDescription')}</p>
          </div>

          <div className="action-card" onClick={() => navigate('/restaurant/reports')}>
            <div className="action-icon">📊</div>
            <h3>{t('reports')}</h3>
            <p>{t('reportsDescription')}</p>
          </div>

          <div className="action-card" onClick={() => navigate('/restaurant/wallet')}>
            <div className="action-icon">💰</div>
            <h3>{t('wallet')}</h3>
            <p>{t('walletDescription')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RestaurantDashboard;